import React from 'react';
import Icon from '../../../components/AppIcon';

const PricingSection = ({ formData, updateFormData }) => {
  const handlePriceChange = (type, value) => {
    updateFormData('pricing', { ...formData?.pricing, [type]: parseInt(value) });
  };

  const getPriceTag = (value, type) => {
    if (type === 'nightly') {
      if (value >= 120) return { label: 'Premium', color: 'bg-success text-success-foreground' };
      if (value >= 80) return { label: 'High', color: 'bg-primary text-primary-foreground' };
      if (value >= 50) return { label: 'Average', color: 'bg-warning text-warning-foreground' };
      return { label: 'Budget', color: 'bg-muted text-muted-foreground' };
    }
    if (type === 'weekly') {
      if (value >= 600) return { label: 'Premium', color: 'bg-success text-success-foreground' };
      if (value >= 400) return { label: 'High', color: 'bg-primary text-primary-foreground' };
      if (value >= 250) return { label: 'Average', color: 'bg-warning text-warning-foreground' };
      return { label: 'Budget', color: 'bg-muted text-muted-foreground' };
    }
    if (type === 'monthly') {
      if (value >= 2000) return { label: 'Premium', color: 'bg-success text-success-foreground' };
      if (value >= 1500) return { label: 'High', color: 'bg-primary text-primary-foreground' };
      if (value >= 800) return { label: 'Average', color: 'bg-warning text-warning-foreground' };
      return { label: 'Budget', color: 'bg-muted text-muted-foreground' };
    }
    return { label: 'Budget', color: 'bg-muted text-muted-foreground' };
  };

  const PriceSlider = ({ label, type, min, max, step, value, tooltip }) => {
    const safeValue = value || min;
    const priceTag = getPriceTag(safeValue, type);
    
    return (
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <label className="text-sm font-medium text-foreground">{label}</label>
          <div className="flex items-center space-x-2">
            <span className="text-lg font-bold text-primary">${safeValue}</span>
            <span className={`px-2 py-1 rounded-full text-xs font-medium ${priceTag?.color}`}>
              {priceTag?.label}
            </span>
          </div>
        </div>
        <div className="relative">
          <input
            type="range"
            min={min}
            max={max}
            step={step}
            value={safeValue}
            onChange={(e) => handlePriceChange(type, e?.target?.value)}
            className="w-full h-2 bg-muted rounded-lg appearance-none cursor-pointer slider"
          />
          <div className="flex justify-between text-xs text-muted-foreground mt-1">
            <span>${min}</span>
            <span>${max}</span>
          </div>
        </div>
        {tooltip && (
          <div className="flex items-center space-x-2">
            <Icon name="Info" size={14} className="text-muted-foreground" />
            <p className="text-xs text-muted-foreground">{tooltip}</p>
          </div>
        )}
      </div>
    );
  };

  // Helper function to safely get numeric value for calculations
  const getNumericValue = (value) => {
    if (value === '' || value === undefined || value === null) return 0;
    return parseFloat(value) || 0;
  };

  const nightly = getNumericValue(formData?.pricing?.nightly);
  const weekly = getNumericValue(formData?.pricing?.weekly);
  const monthly = getNumericValue(formData?.pricing?.monthly);

  return (
    <div className="bg-card rounded-lg border border-border p-6 shadow-sm">
      <div className="flex items-center space-x-3 mb-6">
        <div className="flex items-center justify-center w-10 h-10 bg-primary rounded-lg">
          <Icon name="DollarSign" size={20} color="white" />
        </div>
        <div>
          <h3 className="text-lg font-heading font-bold text-foreground">Pricing Strategy</h3>
          <p className="text-sm text-muted-foreground">Set your rates for different stay lengths</p>
        </div>
      </div>
      <div className="space-y-6">
        <PriceSlider
          label="Nightly Rate"
          type="nightly"
          min={15}
          max={150}
          step={5}
          value={formData?.pricing?.nightly}
          tooltip="Average rate per night for short-term stays"
        />

        <PriceSlider
          label="Weekly Rate"
          type="weekly"
          min={80}
          max={800}
          step={5}
          value={formData?.pricing?.weekly}
          tooltip="Rate for 7-night stays (typically 10-15% discount)"
        />

        <PriceSlider
          label="Monthly Rate"
          type="monthly"
          min={300}
          max={2500}
          step={5}
          value={formData?.pricing?.monthly}
          tooltip="Rate for 30+ day stays (typically 20-30% discount)"
        />
      </div>
      <div className="mt-6 p-4 bg-muted rounded-lg">
        <div className="flex items-center space-x-2 text-sm text-muted-foreground mb-2">
          <Icon name="TrendingUp" size={16} />
          <span className="font-medium">Pricing Insights</span>
        </div>
        <div className="text-xs text-muted-foreground space-y-1">
          <div>Weekly discount: {nightly > 0 ? Math.round((1 - (weekly / 7) / nightly) * 100) : 0}%</div>
          <div>Monthly discount: {nightly > 0 ? Math.round((1 - (monthly / 30) / nightly) * 100) : 0}%</div>
        </div>
      </div>
      <style jsx>{`
        .slider::-webkit-slider-thumb {
          appearance: none;
          height: 20px;
          width: 20px;
          border-radius: 50%;
          background: var(--color-primary);
          cursor: pointer;
          border: 2px solid white;
          box-shadow: 0 2px 4px rgba(0,0,0,0.2);
        }
        .slider::-moz-range-thumb {
          height: 20px;
          width: 20px;
          border-radius: 50%;
          background: var(--color-primary);
          cursor: pointer;
          border: 2px solid white;
          box-shadow: 0 2px 4px rgba(0,0,0,0.2);
        }
      `}</style>
    </div>
  );
};

export default PricingSection;